import { type Block, type Deck, flattenDeck } from "./index.js";

/**
 * What a deck's text can tell before it goes live: placeholders, unread link titles, missing images, polls without
 * options, missing notes. Shared so the studio (with its image checks on top) and the dashboard agree.
 */

/** The line a block is known by: its title, or its question for polls and quizzes. */
export function blockText(b: Block): string {
  return b.type === "poll" || b.type === "quiz" ? b.question : b.title;
}

/** What the studio opens to fix a finding. */
export type Target =
  | { kind: "block"; sectionIndex: number; blockIndex: number }
  | { kind: "section"; sectionIndex: number }
  | { kind: "reference"; refId: string }
  | { kind: "deck" }
  | { kind: "none" };

/** "fix": the audience would see something broken. "check": worth a look, fine to go live with. */
export type Finding = { level: "fix" | "check"; where: string; message: string; target: Target };

// "[To write]", "[To research]", "[Check title]", "[Presenter]", "[Company name]"… but not footnote marks like "[1]".
const PLACEHOLDER = /\[(?:to |check |tbd|todo)[^\]]{0,40}\]|\[[A-Z][A-Za-z ]{2,30}\]/i;
// Fields that hold ids, links or code rather than words the audience reads.
const SKIP = new Set([
  "id",
  "type",
  "kind",
  "code",
  "assetId",
  "filename",
  "from",
  "to",
  "refs",
  "referenceIds",
  "correctOptionId",
  "emphasis",
  "notes",
]);

function placeholders(value: unknown, path: string[] = []): string[] {
  if (typeof value === "string") {
    const m = PLACEHOLDER.exec(value);
    return m ? [m[0]] : [];
  }
  if (Array.isArray(value)) return value.flatMap((v, i) => placeholders(v, [...path, String(i)]));
  if (value && typeof value === "object")
    return Object.entries(value).flatMap(([k, v]) => (SKIP.has(k) ? [] : placeholders(v, [...path, k])));
  return [];
}

/** 1-based slide number of every block, from one pass over the deck. */
function slideNumbers(deck: Deck): Map<string, number> {
  const out = new Map<string, number>();
  flattenDeck(deck).forEach((s, i) => s.kind === "block" && out.set(s.blockId, i + 1));
  return out;
}

/** Everything the deck's text can tell before going live. Image checks are in checkPins. */
export function checkDeck(deck: Deck): Finding[] {
  const out: Finding[] = [];
  const numbers = slideNumbers(deck);
  const label = (b: Block) => `Slide ${numbers.get(b.id) ?? 0} · ${blockText(b).slice(0, 48) || b.type}`;
  const titleBits = { title: deck.title, subtitle: deck.subtitle, event: deck.event, presenter: deck.presenter };
  for (const p of new Set(placeholders(titleBits)))
    out.push({ level: "fix", where: "Title slide", message: `Still says ${p}`, target: { kind: "deck" } });
  if (!deck.presenter.trim())
    out.push({ level: "check", where: "Title slide", message: "No presenter name", target: { kind: "deck" } });

  for (const r of deck.references)
    if (PLACEHOLDER.test(r.title))
      out.push({
        level: "fix",
        where: `Reference · ${hostOf(r.url)}`,
        message: `Title is ${r.title}: the link couldn't be read, so add its title by hand`,
        target: { kind: "reference", refId: r.id },
      });

  let withoutNotes = 0;
  deck.sections.forEach((s, si) => {
    for (const p of new Set(placeholders({ title: s.title, ahead: s.ahead })))
      out.push({
        level: "fix",
        where: `Section ${si + 1} · ${s.title}`,
        message: `Still says ${p}`,
        target: { kind: "section", sectionIndex: si },
      });
    s.blocks.forEach((b, bi) => {
      const target: Target = { kind: "block", sectionIndex: si, blockIndex: bi };
      const where = label(b);
      for (const p of new Set(placeholders(b))) out.push({ level: "fix", where, message: `Still says ${p}`, target });
      if (b.type === "screenshot") {
        if (!b.assetId) out.push({ level: "fix", where, message: "No image yet: upload one or pick it", target });
        else if (b.pins.length === 0)
          out.push({ level: "check", where, message: "No discussion pins on the image", target });
      }
      if (b.type === "video" && !b.assetId)
        out.push({ level: "fix", where, message: "No clip yet: upload one or pick it", target });
      if (b.type === "poll" && (b.kind === "single" || b.kind === "multi") && b.options.length < 2)
        out.push({ level: "fix", where, message: "A choice poll needs at least two options", target });
      if (b.type === "quiz" && !b.explanation)
        out.push({ level: "check", where, message: "No explanation to show with the answer", target });
      if (b.type !== "references" && !b.notes?.trim()) withoutNotes++;
    });
  });
  if (withoutNotes > 0)
    out.push({
      level: "check",
      where: "Speaker notes",
      message: `${withoutNotes} slide${withoutNotes === 1 ? " has" : "s have"} no speaker notes`,
      target: { kind: "none" },
    });
  return out;
}

/** The site a link points at, without "www.", for captions. */
export function hostOf(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url;
  }
}
